import React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { getEndpoint, getEndpointFunc } from "../../context/AppContext.jsx";
import { useSave } from '../../context/SaveContext.jsx';
import { useApp } from '../../context/AppContext.jsx';
import { MyButton, MySelectBoxLabel } from '../ui/UI.jsx';
import {
	artifactType,
	enchantmentType,
	landType,
	spellType,
	creatureType,
	battleType,
	cardKeyword,
	frameEffect
} from '../../data/mtgjson/mtgjson.js';
import { restGetJson } from "@MyLib/System/Rest.js";

export const isCardTransform = (card) => {
	return card.layout == "transform" || card.layout == "modal_dfc" || card.layout == "meld";
};

export const getCardFace = (card) => {
	return (card.side == "b" && isCardTransform(card)) ? "back" : "front";
};

const ImageToggle = ({ name, src, checked, onChange }) => 
{
	const onClick = () => {
		onChange({
			target: {
				name: name,
				checked: !checked
			}
		});
	};

	return (
		<div
			style={{ cursor: "pointer" }}
			onClick={onClick}
		>
			<img
				src={src}
				alt={name}
				style={{
					width: "32px",
					height: "32px",
					opacity: checked ? 1.0 : 0.3, // ✅ オフ時に薄く
				}}
			/>
		</div>
	);
};

export const WhiteSymbol = ({checked, onChange}) => {
	return (<ImageToggle name="white" src="./img/White.png" checked={checked} onChange={onChange} />);
};

export const BlueSymbol = ({checked, onChange}) => {
	return (<ImageToggle name="blue" src="./img/Blue.png" checked={checked} onChange={onChange} />);
};

export const BlackSymbol = ({checked, onChange}) => {
	return (<ImageToggle name="black" src="./img/Black.png" checked={checked} onChange={onChange} />);
};

export const RedSymbol = ({checked, onChange}) => {
	return (<ImageToggle name="red" src="./img/Red.png" checked={checked} onChange={onChange} />);
};

export const GreenSymbol = ({checked, onChange}) => {
	return (<ImageToggle name="green" src="./img/Green.png" checked={checked} onChange={onChange} />);
};

export const ColorlessSymbol = ({checked, onChange}) => {
	return (<ImageToggle name="colorless" src="./img/Colorless.png" checked={checked} onChange={onChange} />);
};

export const CardTypeList = () => {
	const { t } = useTranslation();
	return [
		{ key: "artifact", value: t("CardType_Artifact") },
		{ key: "enchantment", value: t("CardType_Enchantment") },
		{ key: "creature", value: t("CardType_Creature") },
		{ key: "land", value: t("CardType_Land") },
		{ key: "instant", value: t("CardType_Instant") },
		{ key: "sorcery", value: t("CardType_Sorcery") },
		{ key: "planeswalker", value: t("CardType_Planeswalker") },
	];
};

export const SupertypeList = () => {
	const { t } = useTranslation();
	return [
		{ key: "null", value: t("Supertype_null") },
		{ key: "legendary", value: t("Supertype_legendary") },
		{ key: "world", value: t("Supertype_world") },
		{ key: "basic", value: t("Supertype_basic") },
	];
};

export const CardRarityList = () => {
	const { t } = useTranslation();
	return [
		{ key: "common", value: t("Rarity_common") },
		{ key: "uncommon", value: t("Rarity_uncommon") },
		{ key: "rare", value: t("Rarity_rare") },
		{ key: "mythic", value: t("Rarity_mythic") },
	];
};

const SubtypeList = (subtypeList) => {
	const { t } = useTranslation();
	return Object.values(subtypeList).map(keyword => ({ key: keyword, value: t(`Subtype_${keyword}`) }));
};
export const ArtifactTypeList = () => { return SubtypeList(artifactType); }
export const EnchantmentTypeList = () => { return SubtypeList(enchantmentType); }
export const LandTypeList = () => { return SubtypeList(landType); }
export const SpellTypeList = () => { return SubtypeList(spellType); }
export const CreatureTypeList = () => { return SubtypeList(creatureType); }
export const BattleTypeList = () => { return SubtypeList(battleType); }

export const CardKeywordList = () => {
	const { t } = useTranslation();
	return Object.values(cardKeyword).map(keyword => ({ key: keyword, value: t(`Keyword_${keyword}`) }));
};

export const FrameEffectList = () => {
	const { t } = useTranslation();
	return Object.values(frameEffect).map(frameEffect => ({ key: frameEffect, value: t(`FrameEffect_${frameEffect}`) }));
};

export const CardLanguageList = () => {
	return [
		{ key: "English", value: "en" },
		{ key: "Japanese", value: "jp" },
	];
};

export const CardLanguage = ({value, onChange, disabledLabel=false}) => {
	const { t } = useTranslation();
	const onChangeEvent = (e) => {
		onChange?.(e);
		e.target.blur();
	};
	return (
		<MySelectBoxLabel
			name={disabledLabel ? null : t("Card_Language")}
			itemList={CardLanguageList().map(item => ({value:item.key, text:item.value}))}
			value={value}
			onChange={onChangeEvent}
		/>
	);
}

export const CardAvailabilityList = () => {
	const { t } = useTranslation();
	return [
		{ key: "paper", value: t("CardAvailability_paper") },
		{ key: "arena", value: t("CardAvailability_arena") },
		{ key: "mtgo", value: t("CardAvailability_mtgo") },
	];
};

const getFallbackImages = (lang, setCode, face, uuid) => {
	const getImage = (lang, setCode, face, uuid) => {
		return `${getEndpointFunc()}/card-image/${lang}/normal/${setCode}/${face}/${uuid}`;
	};
	return [
		getImage(lang, setCode, face, uuid),
		getImage("English", setCode, face, uuid),
		"./img/normal.jpg"
	];
};

export const useCardSrc = () =>
{
	const [errorCount, setErrorCount] = useState(0);
	
	return {
		getSrc: (language, setCode, face, uuid) => {
			const fallbackList = getFallbackImages(language, setCode, face, uuid);
			return fallbackList[Math.min(errorCount, fallbackList.length-1)];
		},
		onError: () => {
			setErrorCount(errorCount + 1);
		},
		reset: () => {
			setErrorCount(0);
		}
	};
};

export const CardImage = ({card, face, uuid, language, onTransform}) =>
{
	const cardSrc = useCardSrc();

	useEffect(() => {
		cardSrc.reset();
	}, [uuid]);

	const getRotation = (card) => {
		const subtypes = card.subtypes.split(",").map(s => s.trim());
		const targets = ["Siege", "Room"];
		if (targets.some(t => subtypes.includes(t)) || card.layout == 'split') {
			return "rotate(90deg)";
		}
		return "none";
	};

	return (
		<div
			className='position-relative'
			style={{
				width: "380px",
				height: "380px",
				overflow: "hidden"
			}}
		>
			<img
				src={cardSrc.getSrc(language, card.setCode, face, uuid)}
				onError={cardSrc.onError}
				draggable={false}
				className="rounded"
				style={{
					width: "100%",
					height: "100%",
					objectFit: "contain",
					transform: getRotation(card),
					transformOrigin: "center",
				}}
				onClick={()=>{}}
			/>
			{onTransform &&
				<div className="position-absolute bottom-0 end-0">
					<MyButton onClick={onTransform} text="←" />
				</div>
			}
		</div>
	);
	/*
	return (
		<img
			src={fallbackList[errorCount]}
			onError={() => {
				if (errorCount < fallbackList.length - 1) {
					setErrorCount(errorCount + 1);
				}
			}}
			style={{
				height: "300px",
				transform: "rotate(90deg) translateY(-100%)",
				transformOrigin: "top left",
			}}
			className="d-block"
		/>
	);
	*/
};

const ManaSymbol = ({ type }) => {
	const colors = {
		W: './img/White.png',
		U: './img/Blue.png',
		B: './img/Black.png',
		R: './img/Red.png',
		G: './img/Green.png',
		C: './img/Colorless.png',
	};

	return (
		<img 
			src={colors[type]}
			alt={type} 
			style={{ width: '1.2em', height: '1.2em', verticalAlign: 'middle', margin: '0 2px' }} 
		/>
	);
};

export const ManaText = ({ text, wrap=true }) => {
	//const parts = text.split(/({[WUBRGC\d]+})/g);
	const parts = (text ? text.replace(/\\n/g, "\n") : "---").split(/({[WUBRGC]+})/g);

	return (
		<span style={{ whiteSpace: wrap ? "pre-wrap" : "nowrap" }}>
			{parts.map((part, index) => {
				// 正規表現にマッチした部分（例: {W}）を判定
				//const match = part.match(/{(.*)}/);
				const match = part.match(/{([WUBRGC]+)}/);
				if (match) {
					const symbol = match[1]; // 中身の "W" や "B" を取り出す
					return <ManaSymbol key={index} type={symbol} />;
				}
				// 普通の文字列はそのまま返す
				return part;
			})}
		</span>
	);
};

const CardTextFrontBack = ({card, other}) => {
	const {t, i18n} = useTranslation();
	const {filters} = useSave();
	const {cardSets, getCardSets} = useApp();
	
	const [name, setName] = useState("");
	const [type, setType] = useState("");
	const [text, setText] = useState("");
	const [manaCost, setManaCost] = useState("");
	const [name2, setName2] = useState("");
	const [type2, setType2] = useState("");
	const [text2, setText2] = useState("");
	const [manaCost2, setManaCost2] = useState("");

	useEffect(() => {
		getCardSets(i18n.language);
	}, [i18n.language]);

	useEffect(() =>{
		if (card) {
			(async() => {
				try {
					const query = new URLSearchParams();
					query.append("uuid", card.uuid);
					query.append("language", filters.language);
					const data = await restGetJson(`${getEndpoint()}/mtg-card-foregin?${query.toString()}`);
					setName(data.faceName ?? data.name);
					setType(data.type);
					setText(data.text);
					setManaCost(card.manaCost);
				} catch (err) {
					console.error(err);
				}
			})();
		}
	}, [card]);

	useEffect(() =>{
		if (other) {
			(async() => {
				try {
					const query = new URLSearchParams();
					query.append("uuid", other.uuid);
					query.append("language", filters.language);
					const data = await restGetJson(`${getEndpoint()}/mtg-card-foregin?${query.toString()}`);
					setName2(data.faceName);
					setType2(data.type);
					setText2(data.text);
					setManaCost2(other.manaCost);
				} catch (err) {
					console.error(err);
				}
			})();
		}
		else {
			setName2("");
			setType2("");
			setText2("");
			setManaCost2("");
		}
	}, [other]);

	return (
		<ul className="list-unstyled m-0">
			<li>
				<div className="d-flex gap-3">
					<strong>{name}</strong>
					<ManaText text={manaCost} />
				</div>
			</li>
			<li>{type}</li>
			<li className="border-top my-1" />
			<li><ManaText text={text} /></li>
			<li className="border-top border-2 my-1" />
			{other && <li>
				<div className="d-flex gap-3">
					<strong>{name2}</strong>
					<ManaText text={manaCost2} />
				</div>
			</li>}
			{other && <li>{type2}</li>}
			{other && <li className="border-top my-1" />}
			{other && <li><ManaText text={text2} /></li>}
			{other && <li className="border-top border-2 my-1" />}
			<li>{cardSets.find(set => set.code === card.setCode)?.name ?? ""} [{card.setCode}]</li>
		</ul>
	);
}

export const CardTextGroup = ({card, other, transform}) => {
	if (isCardTransform(card)) {
		return (<CardTextFrontBack card={(transform=="front" ? card : other)} />);
	}
	else if (card.otherFaceIds) {
		return (<CardTextFrontBack card={card} other={other} />);
	}
	else {
		return (<CardTextFrontBack card={card} />);
	}
};
